import React, { useEffect, useState, useRef } from "react";
import { Search } from "lucide-react";
import { User as UserType } from "@repo/types";
import {
    Avatar,
    cn,
    ComboBox,
    Description,
    Input,
    Label,
    ListBox,
} from "@heroui/react";

interface UserSearchProps {
    searchFn: (query: string) => Promise<{ data: { content: UserType.UserSearchItem[] } }>;
    value?: string | null;
    onSelect: (user: UserType.UserSearchItem) => void;
    placeholder?: string;
    disabled?: boolean;
    className?: string;
    onInputChange?: (val: string) => void;
    onEnter?: (val: string) => void;
    clearAfterSelect?: boolean;
}

export const UserSearch: React.FC<UserSearchProps> = ({
    searchFn,
    value,
    onSelect,
    placeholder = "搜索用户",
    disabled = false,
    className = "",
    onInputChange,
    onEnter,
    clearAfterSelect = false,
}) => {
    const [userList, setUserList] = useState<UserType.UserSearchItem[]>([]);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        if (value) {
            debounceRef.current = setTimeout(() => {
                searchFn(value).then(({ data }) => {
                    setUserList(data.content);
                });
            }, 400);
        } else {
            setUserList([]);
        }
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, [value, searchFn]);

    const handleInputChange = (val: string) => {
        if (onInputChange) onInputChange(val);
    };

    const handleSelectionChange = (key: React.Key | null) => {
        if (key == null) return;
        const selectedUser = userList.find((user) => user.uid === String(key)) as
            | UserType.UserSearchItem
            | undefined;
        if (!selectedUser) return;
        onSelect(selectedUser);
        if (clearAfterSelect && onInputChange) {
            onInputChange("");
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (!value) return;
        if (e.key === "Enter" && onEnter) {
            onEnter(value);
        }
    };

    /** 异步搜索结果已由接口过滤，不在客户端再次过滤 */
    const passthroughFilter = () => true;

    return (
        <ComboBox<UserType.UserSearchItem>
            allowsCustomValue
            allowsEmptyCollection
            aria-label="用户选择器"
            className={cn("w-full", className)}
            defaultFilter={passthroughFilter}
            inputValue={value ?? ""}
            isDisabled={disabled}
            items={userList}
            menuTrigger="input"
            onInputChange={handleInputChange}
            onChange={handleSelectionChange}
        >
            <ComboBox.InputGroup className="relative flex w-full min-w-0 items-center gap-2">
                <Search
                    aria-hidden
                    size={16}
                    className="pointer-events-none absolute left-3 top-1/2 z-10 shrink-0 -translate-y-1/2 text-gray-500 dark:text-gray-300"
                />
                <Input
                    className="w-full pl-9"
                    onKeyDown={handleKeyDown}
                    placeholder={placeholder}
                />
                <ComboBox.Trigger className="shrink-0" />
            </ComboBox.InputGroup>
            <ComboBox.Popover className="max-h-[400px]">
                <ListBox<UserType.UserSearchItem>
                    renderEmptyState={() => (
                        <div className="px-3 py-2 text-sm text-gray-500">
                            没有找到用户，检查一下是否正确哦～
                        </div>
                    )}
                >
                    {(item) => (
                        <ListBox.Item id={item.uid} textValue={item.nickname}>
                            <div className="flex items-center gap-2 py-1">
                                <Avatar className="size-9 shrink-0 rounded-lg">
                                    <Avatar.Image src={item.avatar_url} />
                                    <Avatar.Fallback>
                                        <img
                                            alt="default avatar"
                                            className="size-full object-cover"
                                            src="/defaultAvatar.png"
                                        />
                                    </Avatar.Fallback>
                                </Avatar>
                                <div className="min-w-0 flex-1">
                                    <Label className="block truncate">{item.nickname}</Label>
                                    <Description className="text-xs">{item.uid}</Description>
                                </div>
                            </div>
                            <ListBox.ItemIndicator />
                        </ListBox.Item>
                    )}
                </ListBox>
            </ComboBox.Popover>
        </ComboBox>
    );
};
